import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Avatar,
  Button,
  Input,
  Textarea,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import { ChatState } from "../Context/ChatProvider"; // Import the ChatState context
import axios from "axios"; // Import axios for making API calls
import { useNavigate } from "react-router-dom";

const UserProfilePage = () => {
  const { user, loading } = ChatState(); // Get logged-in user from context
  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState(true);

  // State variables to hold profile information
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); // New state for confirm password
  const [pic, setPic] = useState("");
  const [description, setDescription] = useState("");
  const [updateLoading, setUpdateLoading] = useState(false); // Loading state for update button
  const toast = useToast(); // Toast for notifications

  // Protect this page - require authentication
  useEffect(() => {
    if (loading) return; // Wait for user data to load from ChatProvider

    if (!user) {
      navigate("/login");
      return;
    }

    // Check if customer is banned
    if (user.role === "customer" && user.isBanned) {
      navigate("/customer-banned");
      return;
    }

    // Also check if vendor is approved
    if (user.role === "vendor") {
      if (user.isApproved === "pending") {
        navigate("/vendor-pending");
        return;
      } else if (user.isApproved === "rejected") {
        navigate("/vendor-rejected");
        return;
      }
    }

    // Set user data from context
    setName(user.name);
    setEmail(user.email);
    setPic(user.pic);
    setDescription(user.description || "");

    setPageLoading(false);
  }, [navigate, user, loading]);

  if (pageLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" h="100vh">
        <Spinner size="xl" />
      </Box>
    );
  }

  // Handle profile update
  const handleUpdate = async () => {
    // Check if password and confirm password match
    if (password && password !== confirmPassword) {
      toast({
        title: "Passwords do not match.",
        description: "Please ensure that both password fields match.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setUpdateLoading(true);
    try {
      // Create an object with updated user information
      const updatedUser = {
        name,
        email,
        password: password || undefined, // Only send password if it's provided
        pic,
        description,
      };

      // Make the API call to update the user information
      const token = localStorage.getItem("authToken");
      const response = await axios.put(`/api/user/update`, updatedUser, {
        headers: {
          Authorization: `Bearer ${token}`, // Include the user's token for authentication
        },
      });

      // Show success notification
      toast({
        title: "Profile updated.",
        description: "Your information has been updated successfully.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      // Handle errors and show error notification
      toast({
        title: "Error updating profile.",
        description: error.response?.data?.message || "Please try again later.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    setUpdateLoading(false); // Reset loading state
  };

  // Handle profile picture upload
  const handlePicUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Logic for uploading the picture
      // For example, you can use Cloudinary API or other services to handle image uploads
      setPic(URL.createObjectURL(file)); // Display the uploaded picture temporarily
    }
  };

  return (
    <Box p={8} textAlign="center">
      <Avatar size="2xl" name={name} src={pic} mt={4} mb={4} />
      <Heading fontSize="3xl" mb={4}>
        Profile Details
      </Heading>

      {/* Name Input */}
      <Input
        placeholder="Change Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        mb={4}
      />

      {/* Email Input */}
      <Input
        placeholder="Change Email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        mb={4}
      />

      {/* Password Input */}
      <Input
        placeholder="Change Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        mb={4}
      />

      {/* Confirm Password Input */}
      <Input
        placeholder="Confirm Password"
        type="password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        mb={4}
      />

      {/* Picture Upload Input */}
      <Input
        type="file"
        accept="image/*"
        onChange={handlePicUpload}
        mb={4}
        p={1.5} // Add padding for better UI
      />

      {/* Description Textarea */}
      <Textarea
        placeholder="Enter a brief description about yourself"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        mb={4}
      />

      <Button
        colorScheme="blue"
        onClick={handleUpdate}
        isLoading={updateLoading} // Show loading state on button
      >
        Update Profile
      </Button>
    </Box>
  );
};

export default UserProfilePage;
